import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { compact, omit } from "lodash-es";
import moment from "moment";
import PropTypes from "prop-types";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";

// Helpers
import RowFeedback from "@/Common/helpers/RowFeedback";
import { accountFormat } from "@/Common/helpers/number";
import { dateValidation, emptyValidation } from "@/Layouts/components/Tables/DataSmartTable/validation/TableValidations";
import { getDateInUTC, getFormattedDate } from "@dealerwave/dw-layer-date-converter";

// Hooks/Contexts
import { useAuthorization } from "@/Common/hooks/Authorization/Authorization";
import { useMotivators } from "@Motivators/context/Common/MotivatorsContext";

// APIs
import { getUsersSelect } from "@/Common/api/users/queries";
import { createServiceSpiffAward, deleteServiceSpiffAward, updateServiceSpiffAward } from "@/Motivators/api/awards";

// Components
import DataTableDateTimePicker from "@/Layouts/components/DateTimePicker/DataTableDateTimePicker";
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";
import DataSmartTable from "@/Layouts/components/Tables/DataSmartTable";
import HelpTooltip from "@/Layouts/components/Tables/DataSmartTable/additional/HelpTooltip";
import CommonToolbar from "@/Layouts/components/Tables/DataSmartTable/toolbar/CommonToolbar";
import NoRowsOverlay from "@Layouts/components/Tables/DataSmartTable/additional/NoRowsOverlay";

// MUI
import { Box, FormControl, IconButton, Menu, TextField, Typography } from "@mui/material";
import { GridActionsCellItem, GridRowModes, useGridApiContext, useGridApiRef } from "@mui/x-data-grid-premium";

// Icons
import { SaveIconComponent } from "@/Layouts/components/Tables/DataSmartTable/additional/SaveIconComponent";
import CancelIcon from "@mui/icons-material/Close";

// Constants
import actionsList from "@/Constants/actionsList";
import { DATE_FORMAT } from "@/Constants/date";
import { TAILWIND_FOR_DATA_SMART_TABLE } from "@/Constants/tables";
import { HELP_TOOLTIP_TITLE } from "@/Motivators/constants";

import { DepartmentEnum } from "@/Common/constants/DepartmentEnum";
import { columnProps } from "@/Common/constants/tableColumnProps";
import { useLocalPositions } from "@/Common/hooks/Positions/useLocalPositions";
import { getUserRepairOrderNumbers } from "@/Motivators/api/spiffs/queries";
import { customFullName } from "@/Motivators/helpers/spiffs/customData";
import clsx from "clsx";

InputEditDatetimeCellBonus.propTypes = {
	id: PropTypes.string,
	field: PropTypes.string,
	error: PropTypes.bool,
	value: PropTypes.any,
	itemStartDate: PropTypes.string,
	itemEndDate: PropTypes.string,
	commissionCycleStartDate: PropTypes.string,
	commissionCycleEndDate: PropTypes.string,
};

function InputEditDatetimeCellBonus(props) {
	const { id, field, itemStartDate, itemEndDate, commissionCycleStartDate, commissionCycleEndDate, error } = props;
	const apiRef = useGridApiContext();
	const [spiffDateValue, setSpiffDateValue] = useState(props.value);

	const onChangeBonusHandle = (value) => {
		const inputValue = moment(value).format();
		apiRef.current.setEditCellValue({ id, field, value: new Date(inputValue), debounceMs: 200 });
		setSpiffDateValue(new Date(inputValue)); // This is to update the value in the input field
	};
	const effectiveDateRangeTo = moment(itemEndDate).startOf("day"); // This is the end date of the effective date range
	const today = moment().startOf("day");

	const minDateBonus = moment(Math.max(new Date(itemStartDate), moment(commissionCycleStartDate)));
	const maxDateTimeBonus = moment(Math.min(today, effectiveDateRangeTo, moment(commissionCycleEndDate)));
	return (
		<DataTableDateTimePicker
			value={spiffDateValue}
			minDateTime={minDateBonus}
			maxDateTime={maxDateTimeBonus}
			defaultCalendarMonth={moment(commissionCycleStartDate)}
			disableHighlightToday
			format={DATE_FORMAT.usDateSlash}
			error={!spiffDateValue || error}
			disableElevation
			views={["year", "month", "day"]}
			hideTabs
			onChange={onChangeBonusHandle}
			viewRenderers={{
				hours: null,
				minutes: null,
				seconds: null,
			}}
		/>
	);
}

function InputEditUserCellBonus(props) {
	const apiRef = useGridApiContext();
	const { id, field, positions, locationId } = props;
	const { positions: managedPositions, isLoading: isLoadingPositions } = useLocalPositions({ positionType: DepartmentEnum.SERVICE });

	const [recipient, setRecipient] = useState(null);

	const eligiblePositions = positions ?? managedPositions ?? [];

	const { data: recipientData, isLoading: isLoadingRecipient } = useQuery({
		queryKey: ["recipientSpiffEntry", locationId],
		queryFn: async () => getUsersSelect(locationId, eligiblePositions, "ACTIVE"),
		staleTime: Infinity,
		enabled: !!eligiblePositions.length,
	});

	const isLoading = isLoadingRecipient || isLoadingPositions;

	const handleChange = (values) => {
		if (values) {
			apiRef.current.setEditCellValue({ id, field, value: values, debounceMs: 200 });
			apiRef.current.setEditCellValue({ id, field: "repairOrders", value: [], debounceMs: 200 });
			setRecipient(values);
		}
	};

	useEffect(() => {
		if (!props.row.recipient) {
			return;
		}
		setRecipient(props.row.recipient);
	}, [props.row.recipient]);

	return (
		<SmartSelector
			showBy={"fullName"}
			sortBy={"fullName"}
			options={recipientData}
			selected={recipient}
			inputProps={{
				placeholder: "Recipients",
				classes:
					"[&_.MuiInputBase-root]:!min-h-[26px] [&_.MuiInputBase-root]:!py-[3px] [&_.MuiInputBase-root_.MuiInputBase-input]:!rounded-none",
			}}
			listBoxProps={{
				sx: { maxHeight: "223px" },
			}}
			popperProps={{
				sx: { minWidth: "190px", maxWidth: "240px" },
			}}
			error={!recipient}
			loading={isLoading}
			onChangeAction={handleChange}
		/>
	);
}

InputEditUserCellBonus.propTypes = {
	id: PropTypes.string,
	field: PropTypes.string,
	error: PropTypes.bool,
	row: PropTypes.object,
};

InputEditInputCell.propTypes = {
	id: PropTypes.string,
	field: PropTypes.string,
	error: PropTypes.bool,
	value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};
function InputEditInputCell(props) {
	const apiRef = useGridApiContext();
	if (!apiRef.current) {
		apiRef.current = {};
	}
	const { id, field, error } = props;
	const [value, setValue] = useState(props.value || "");

	const handleChange = (event) => {
		let inputValue = event.target.value;
		if (inputValue > 10000) {
			inputValue = 10000;
		}
		// `debounceMs` is important to avoid the input to be updated too often.
		apiRef.current.setEditCellValue({ id, field, value: inputValue, debounceMs: 200 });
		setValue(inputValue);
	};

	const handleKeyDown = (event) => {
		if (event.keyCode === 8) {
			return;
		}
		const isNumber = /^[0-9.]$/i.test(event.key);
		if (!isNumber && !event.key.match("Arrow")) {
			event.preventDefault();
		}
	};

	return (
		<FormControl fullWidth>
			<TextField
				variant="outlined"
				className="text-input-number text-input-error"
				value={value}
				placeholder={"0.00"}
				InputProps={{
					className: "!h-[27px]",
					startAdornment: (
						<IconButton aria-label="Dollar" edge="start">
							$
						</IconButton>
					),
				}}
				inputProps={{
					maxLength: 7,
				}}
				onChange={handleChange}
				onKeyDown={handleKeyDown}
				error={error || !value}
			/>
		</FormControl>
	);
}
SpiffBonusTable.propTypes = {
	definitionId: PropTypes.string,
	locationId: PropTypes.string,
	data: PropTypes.array,
	effectiveDateRangeFrom: PropTypes.string,
	effectiveDateRangeTo: PropTypes.string,
	isEditable: PropTypes.bool,
};

const InputEditRepairOrdersCell = (props) => {
	const { id, field, row, commissionCycleId, definitionId, dateRanges, onOpenAction, onCloseAction } = props;
	const apiRef = useGridApiContext();
	const location = useSelector((state) => state.location.location);
	// Get UserId
	const userId = useMemo(() => row?.recipient?._id, [row?.recipient?._id]);
	const prevUserId = useRef(userId);

	const currentRepairOrders = useMemo(() => {
		const editRow = apiRef.current.state.editRows[id];
		const editValue = editRow?.repairOrders?.value;
		return editValue ?? row?.repairOrders ?? [];
	}, [apiRef, id, row?.repairOrders]);

	const selectedRepairOrderNumbers = useMemo(() => currentRepairOrders.map((d) => d.repairOrderNumber) ?? [], [currentRepairOrders]);

	const {
		data: userRepairOrderNumbers = [],
		isLoading,
		isFetching,
	} = useQuery({
		queryKey: ["userRepairOrderNumbers", location?.locationId, definitionId, userId],
		queryFn: async () => {
			return await getUserRepairOrderNumbers(location?.locationId, userId, commissionCycleId, { dateRanges });
		},
		staleTime: Infinity,
		enabled: !!location?.locationId && !!userId,
		placeholderData: [],
	});

	const availableOptions = useMemo(() => {
		return [...userRepairOrderNumbers].filter((option) => !selectedRepairOrderNumbers.includes(option.repairOrderNumber));
	}, [selectedRepairOrderNumbers, userRepairOrderNumbers]);

	const handleOnOpenAction = () => {
		if (!onOpenAction) {
			return;
		}
		onOpenAction();
	};

	const handleOnCloseAction = () => {
		if (!onCloseAction) {
			return;
		}
		onCloseAction();
	};

	const handleChange = (values = []) => {
		const repairOrderNumbers = values?.map((deal) => deal.repairOrderNumber) || [];
		const vinNumbers = values?.map((deal) => deal.vinNumber) || [];

		apiRef.current.setEditCellValue({
			id,
			field: "repairOrders",
			value: values,
		});

		apiRef.current.setEditCellValue({
			id,
			field: "repairOrderNumbers",
			value: repairOrderNumbers,
		});

		apiRef.current.setEditCellValue({
			id,
			field: "vinNumbers",
			value: vinNumbers,
		});
	};

	useEffect(() => {
		// Only clear when userId actually changes (not on first render)
		if (prevUserId.current && prevUserId.current !== userId && currentRepairOrders.length > 0) {
			apiRef.current.setEditCellValue({
				id,
				field: "repairOrders",
				value: [],
			});

			apiRef.current.setEditCellValue({
				id,
				field: "repairOrderNumbers",
				value: [],
			});

			apiRef.current.setEditCellValue({
				id,
				field: "vinNumbers",
				value: [],
			});
		}
		prevUserId.current = userId;
	}, [userId, id, apiRef, currentRepairOrders]);

	const isSpiffDateSelected = row?.spiffDate !== null;

	return (
		<SmartSelector
			multiple
			showBy={"repairOrderNumber"}
			selectBy={"repairOrderNumber"}
			options={availableOptions}
			selected={currentRepairOrders}
			inputProps={{
				placeholder: "Select ro numbers...",
				classes: clsx({
					"[&_.MuiInputBase-root]:!min-h-[26px] [&_.MuiInputBase-root]:!py-[3px] stocks-area": true,
					"[&_.MuiInputBase-root>.MuiButtonBase-root>span.MuiChip-label]:!font-sans": true,
					"[&_.MuiInputBase-root>.MuiButtonBase-root.MuiChip-root]:!h-[19px]": true,
					"[&_.MuiInputBase-root>.MuiAutocomplete-tag.MuiAutocomplete-tagSizeSmall]:!h-[19px]": true,
					"[&_.MuiInputBase-root>.MuiAutocomplete-tag.MuiAutocomplete-tagSizeSmall]:!leading-[1.4]": true,
				}),
			}}
			listBoxProps={{
				sx: { maxHeight: "220px" },
			}}
			clearable
			disabled={isFetching || !isSpiffDateSelected}
			error={!currentRepairOrders?.length || !isSpiffDateSelected}
			loading={isLoading}
			onChangeAction={handleChange}
			onOpenAction={handleOnOpenAction}
			onCloseAction={handleOnCloseAction}
		/>
	);
};

const InputEditVinsCell = (props) => {
	const { id, field, row, commissionCycleId, definitionId, dateRanges, onOpenAction, onCloseAction } = props;
	const apiRef = useGridApiContext();
	const location = useSelector((state) => state.location.location);
	// Get UserId
	const userId = useMemo(() => row?.recipient?._id, [row?.recipient?._id]);
	const prevUserId = useRef(userId);

	const currentRepairOrders = useMemo(() => {
		const editRow = apiRef.current.state.editRows[id];
		const editValue = editRow?.repairOrders?.value;
		return editValue ?? row?.repairOrders ?? [];
	}, [apiRef, id, row?.repairOrders]);

	const selectedVinNumbers = useMemo(() => currentRepairOrders.map((d) => d.vinNumber) ?? [], [currentRepairOrders]);

	const {
		data: userRepairOrderNumbers = [],
		isLoading,
		isFetching,
	} = useQuery({
		queryKey: ["userRepairOrderNumbers", location?.locationId, definitionId, userId],
		queryFn: async () => {
			return await getUserRepairOrderNumbers(location?.locationId, userId, commissionCycleId, { dateRanges });
		},
		staleTime: Infinity,
		enabled: !!location?.locationId && !!userId,
		placeholderData: [],
	});

	const availableOptions = useMemo(() => {
		return [...userRepairOrderNumbers].filter((option) => !selectedVinNumbers.includes(option.vinNumber));
	}, [selectedVinNumbers, userRepairOrderNumbers]);

	const handleOnOpenAction = () => {
		if (!onOpenAction) {
			return;
		}
		onOpenAction();
	};

	const handleOnCloseAction = () => {
		if (!onCloseAction) {
			return;
		}
		onCloseAction();
	};

	const handleChange = (values = []) => {
		const repairOrderNumbers = values?.map((deal) => deal.repairOrderNumber) || [];
		const vinNumbers = values?.map((deal) => deal.vinNumber) || [];

		apiRef.current.setEditCellValue({
			id,
			field: "repairOrders",
			value: values,
		});

		apiRef.current.setEditCellValue({
			id,
			field: "repairOrderNumbers",
			value: repairOrderNumbers,
		});

		apiRef.current.setEditCellValue({
			id,
			field: "vinNumbers",
			value: vinNumbers,
		});
	};

	useEffect(() => {
		// Only clear when userId actually changes (not on first render)
		if (prevUserId.current && prevUserId.current !== userId && currentRepairOrders.length > 0) {
			apiRef.current.setEditCellValue({
				id,
				field: "repairOrders",
				value: [],
			});

			apiRef.current.setEditCellValue({
				id,
				field: "repairOrderNumbers",
				value: [],
			});

			apiRef.current.setEditCellValue({
				id,
				field: "vinNumbers",
				value: [],
			});
		}
		prevUserId.current = userId;
	}, [userId, id, apiRef, currentRepairOrders]);

	const isSpiffDateSelected = row?.spiffDate !== null;

	return (
		<SmartSelector
			multiple
			showBy={"vinNumber"}
			selectBy={"vinNumber"}
			options={availableOptions}
			selected={currentRepairOrders}
			inputProps={{
				placeholder: "Select vin numbers...",
				classes: clsx({
					"[&_.MuiInputBase-root]:!min-h-[26px] [&_.MuiInputBase-root]:!py-[3px] stocks-area": true,
					"[&_.MuiInputBase-root>.MuiButtonBase-root>span.MuiChip-label]:!font-sans": true,
					"[&_.MuiInputBase-root>.MuiButtonBase-root.MuiChip-root]:!h-[19px]": true,
					"[&_.MuiInputBase-root>.MuiAutocomplete-tag.MuiAutocomplete-tagSizeSmall]:!h-[19px]": true,
					"[&_.MuiInputBase-root>.MuiAutocomplete-tag.MuiAutocomplete-tagSizeSmall]:!leading-[1.4]": true,
				}),
			}}
			listBoxProps={{
				sx: { maxHeight: "220px" },
			}}
			clearable
			disabled={isFetching || !isSpiffDateSelected}
			error={!currentRepairOrders?.length || !isSpiffDateSelected}
			loading={isLoading}
			onChangeAction={handleChange}
			onOpenAction={handleOnOpenAction}
			onCloseAction={handleOnCloseAction}
		/>
	);
};

function SpiffBonusTable(props) {
	const {
		definitionId,
		spiffDocumentId,
		spiffItem,
		locationId,
		isEditable,
		effectiveDateRangeFrom,
		effectiveDateRangeTo,
		data,
		commissionCycleId,
		commissionCycle,
	} = props;

	const apiRef = useGridApiRef();
	const user = useSelector((state) => state.user.user);
	const newlyAddedRow = useRef(null);
	const [selectorOpen, setSelectorOpen] = useState(false);
	// Auth
	const { useAuth } = useAuthorization();

	// Contexts
	const { identifiers, motivatorType, isLoading } = useMotivators();
	const queryClient = useQueryClient();

	const isGenericSpiff = useMemo(() => spiffItem?.spiffType === "Generic", [spiffItem]);

	const editSpiffAward = useAuth(actionsList.EDIT_SERVICE_SPIFF_AWARDS);
	// Queries / Mutations
	const { mutate: mutateCreateAward } = useMutation({
		mutationFn: (input) => {
			const newInput = { ...input, reward: Number(input.reward) };
			const payPeriodId = resolvePayPeriodId(input.spiffDate);
			return createServiceSpiffAward({
				spiffDefinitionId: definitionId,
				spiffSetupId: spiffDocumentId,
				row: newInput,
				locationId,
				commissionCycleId,
				payPeriodId,
			});
		},
	});

	const { mutate: mutateUpdateAward } = useMutation({
		mutationFn: (input) => {
			const payPeriodId = resolvePayPeriodId(input.spiffDate);
			return updateServiceSpiffAward({
				locationId,
				awardId: input._id,
				row: input,
				spiffDefinitionId: definitionId,
				commissionCycleId,
				payPeriodId,
			});
		},
	});

	const { mutate: mutateDeleteAwardBonus } = useMutation({
		mutationFn: (awardIds) => deleteServiceSpiffAward({ awardIds, locationId }),
	});

	const resolvePayPeriodId = (spiffDate) => {
		const spiffMomentDate = moment.utc(spiffDate);
		return (
			commissionCycle?.payPeriods.find(
				(payPeriod) =>
					payPeriod.status !== "COMPLETED" &&
					spiffMomentDate.isBetween(moment.utc(payPeriod.startDate), moment.utc(payPeriod.endDate), null, "[]")
			)?._id ?? ""
		);
	};

	const [rows, setRows] = useState([]);
	const [rowModesModel, setRowModesModel] = useState({});
	const [selectedRows, setSelectedRows] = useState([]);

	// State to know if some row is loading. So the add and delete buttons can be disabled.
	const [rowLoading, setRowLoading] = useState(false);

	const editModeActive = useMemo(() => Object.values(rowModesModel)?.some(({ mode }) => mode === GridRowModes.Edit), [rowModesModel]);

	const cleanUpEditionModeBonus = (item, operation) => {
		if (item && operation === "delete") {
			setRowModesModel((prev) => ({
				...prev,
				[item._id]: { mode: GridRowModes.View },
			}));
		}
		newlyAddedRow.current = null;
	};

	const handleRowEditStartBonus = (_, event) => {
		if (!isGenericSpiff) {
			return;
		}
		if (editModeActive) {
			event.defaultPrevented = true;
			event.defaultMuiPrevented = true;
		}
	};

	const handleRowEditStop = (_, event) => {
		event.defaultMuiPrevented = true;
	};

	const handleSaveClickBonus = (id) => {
		const rowEdit = apiRef.current.state.editRows;
		const row = Object.values(rowEdit)[0];
		const keys = Object.keys(omit(row, ["repairOrderNumbers", "vinNumbers"]));
		const errors = keys.filter((key) => row[key].error || !row[key].value || (Array.isArray(row[key].value) && !row[key].value.length));

		if (!errors?.length) {
			setRowModesModel((prev) => ({
				...prev,
				[id]: { mode: GridRowModes.View },
			}));
		}
	};

	const handleDeleteClick = async (ids) => {
		setRowLoading(true);

		ids.forEach((id) => {
			RowFeedback.skeleton(id, apiRef).delete().display();
		});

		mutateDeleteAwardBonus(ids, {
			onSuccess: () => {
				setRows((prev) => prev.filter((row) => !ids.includes(row._id)));
				setSelectedRows((prev) => prev.filter((id) => !ids.includes(id)));
			},
			onError: () => {
				ids.forEach((id) => {
					RowFeedback.skeleton(id, apiRef).errorDelete();
				});
			},
			onSettled: () => {
				setRowLoading(false);
			},
		});
	};

	const handleRowModesModelChange = (newModel) => {
		if (!newModel || !isGenericSpiff) {
			return null;
		}
		return setRowModesModel(newModel);
	};

	const handleCancelClick = (id) => {
		setRowModesModel({
			...rowModesModel,
			[id]: { mode: GridRowModes.View, ignoreModifications: true },
		});

		const editedRow = rows.find((row) => row._id === id);
		if (editedRow?.isNew) {
			setRows(rows.filter((row) => row._id !== id));
		}
	};

	const handleAddRow = () => {
		const key = uuidv4();
		const newRow = {
			_id: key,
			isNew: true,
			spiffEntry: "Now",
			spiffDate: null,
			recipient: null,
			reward: null,
			createdBy: user._id,
		};

		setRows((oldRows) => [newRow, ...oldRows]);
		setRowModesModel((oldModel) => ({
			...oldModel,
			[key]: { mode: GridRowModes.Edit },
		}));
	};

	const handleRowSelected = (newSelection) => {
		setSelectedRows(newSelection);
	};

	const handleProcessRowUpdateBonus = async (newRow, oldRow) => {
		const isModified = Object.keys(newRow).some((key) => newRow[key] !== oldRow?.[key]);
		if (!isModified) {
			return oldRow;
		}

		const updatedRow = { ...newRow, isNew: false, updatedBy: user._id, spiffEntry: new Date() };

		const { eligibility, title, type, description, ...resultRow } = updatedRow;

		setRowLoading(true);
		RowFeedback.skeleton(resultRow._id, apiRef).display(); // Here you should save the data to the backend.

		// Mutation operations
		let result = null;
		const mutateOperationBonus = (id, apiRefLocal, resolve) => ({
			onSuccess: (response) => {
				RowFeedback.skeleton(id, apiRefLocal).success();
				resolve?.(response);
			},
			onError: (err) => {
				RowFeedback.skeleton(id, apiRefLocal).error();
				console.log(err);
			},
			onSettled: () => {
				setRowLoading(false);
			},
		});

		if (newRow?.isNew) {
			// Save
			const currentRow = { ...resultRow, recipient: resultRow?.recipient?._id, createdBy: user._id };
			newlyAddedRow.current = { ...currentRow };
			const { isNew, _id, repairOrderNumbers, vinNumbers, ...sendRow } = currentRow;
			const normalizedRow = {
				...sendRow,
				spiffType: props?.spiffItem?.spiffType,
				spiffItemName: props?.spiffItem?.title,
			};
			result = new Promise((resolve) => {
				mutateCreateAward(normalizedRow, mutateOperationBonus(_id, apiRef, resolve));
			});
		} else {
			// Update
			const currentRow = { ...resultRow, recipient: resultRow?.recipient?._id };
			newlyAddedRow.current = { ...currentRow };
			const { isNew, createdBy, createdAt, repairOrderNumbers, vinNumbers, ...sendRow } = currentRow;
			result = new Promise((resolve) => {
				mutateUpdateAward(sendRow, mutateOperationBonus(sendRow._id, apiRef, resolve));
			});
		}

		if (result) {
			await result;
		}
		setRows(rows.map((row) => (row._id === newRow._id ? resultRow : row)));
		return resultRow;
	};

	const handleProcessRowUpdateBonusError = (error) => {
		console.error(error);
	};

	const columns = [
		{ field: "_id", headerName: "ID", width: 50, filterable: false },
		{
			...columnProps,
			field: "spiffEntry",
			headerName: "Last Updated at",
			type: "date",
			editable: false,
			resizable: false,
			sortable: !editModeActive,
			valueFormatter: (value) => getFormattedDate(value, "", DATE_FORMAT.longDatePaddedTime12h),
		},
		{
			...columnProps,
			field: "spiffDate",
			headerName: "Spiff Date",
			type: "date",
			sortable: !editModeActive,
			resizable: false,
			editable: isEditable && editSpiffAward,
			valueGetter: (value) => getDateInUTC(value),
			valueFormatter: (value) => getFormattedDate(value, "", DATE_FORMAT.monthAbbrDatePadded),
			renderEditCell: (params) => (
				<InputEditDatetimeCellBonus
					{...params}
					commissionCycleStartDate={moment.utc(commissionCycle?.startDate).format(DATE_FORMAT.monthAbbrDatePadded)}
					commissionCycleEndDate={moment.utc(commissionCycle?.endDate).format(DATE_FORMAT.monthAbbrDatePadded)}
					itemStartDate={effectiveDateRangeFrom}
					itemEndDate={effectiveDateRangeTo}
				/>
			),
			preProcessEditCellProps: (params) => {
				const valueIsDate = dateValidation(params.props.value); // Check if the value is a date
				const valueExists = emptyValidation(params.props.value); // Check if the value exists
				const dateIsInEffectiveRangeBonus =
					moment(params.props.value).isSameOrBefore(moment().startOf("day")) &&
					moment(params.props.value).isSameOrBefore(moment(commissionCycle?.endDate)?.startOf("day")) &&
					moment(params.props.value).isSameOrAfter(moment(effectiveDateRangeFrom)?.startOf("day")) &&
					moment(params.props.value).isSameOrAfter(moment(commissionCycle?.startDate)?.startOf("day"));
				return { ...params.props, error: !valueExists || !valueIsDate || !dateIsInEffectiveRangeBonus };
			},
		},
		{
			...columnProps,
			field: "recipient",
			headerName: "Recipient",
			resizable: true,
			sortable: !editModeActive,
			editable: isEditable && editSpiffAward,
			renderEditCell: (params) => <InputEditUserCellBonus {...params} positions={spiffItem?.positions} locationId={locationId} />,
			preProcessEditCellProps: (params) => {
				const valueExists = emptyValidation(params.props.value);
				return { ...params.props, error: !valueExists };
			},
			valueFormatter: (value) => customFullName(value),
		},
		{
			...columnProps,
			field: "repairOrders",
			headerName: "Repair Orders",
			editable: props.isEditable && editSpiffAward,
		},
		{
			...columnProps,
			field: "repairOrderNumbers",
			headerName: "RO #",
			description: "",
			sortable: false,
			editable: props.isEditable && editSpiffAward,
			resizable: true,
			cellClassName: "!overflow-visible",
			preProcessEditCellProps: (params) => {
				const editRow = apiRef.current.state.editRows[params.id];
				const repairOrders = editRow?.repairOrders?.value || [];
				const repairOrderNumbers = repairOrders.map((order) => order.repairOrderNumber);
				const valueExists = repairOrderNumbers?.length > 0;
				return { ...params.props, value: repairOrderNumbers, error: !valueExists };
			},
			renderCell: (params) => {
				return <MoreCell {...params} visibleAmount={2} />;
			},
			renderEditCell: (params) => {
				return (
					<InputEditRepairOrdersCell
						{...params}
						commissionCycleId={props?.commissionCycleId}
						definitionId={props.definitionId}
						dateRanges={{
							startDate: props.effectiveDateRangeFrom,
							endDate: props.effectiveDateRangeTo,
						}}
						onOpenAction={() => setSelectorOpen(true)}
						onCloseAction={() => setSelectorOpen(false)}
					/>
				);
			},
		},
		{
			...columnProps,
			field: "vinNumbers",
			headerName: "VIN #",
			description: "",
			sortable: false,
			editable: props.isEditable && editSpiffAward,
			resizable: true,
			cellClassName: "!overflow-visible",
			preProcessEditCellProps: (params) => {
				const editRow = apiRef.current.state.editRows[params.id];
				const repairOrders = editRow?.repairOrders?.value || [];
				const vinNumbers = repairOrders.map((order) => order.vinNumber);
				const valueExists = vinNumbers?.length > 0;
				return { ...params.props, value: vinNumbers, error: !valueExists };
			},
			renderCell: (params) => {
				return <MoreCell {...params} visibleAmount={1} />;
			},
			renderEditCell: (params) => {
				return (
					<InputEditVinsCell
						{...params}
						commissionCycleId={props?.commissionCycleId}
						definitionId={props.definitionId}
						dateRanges={{
							startDate: props.effectiveDateRangeFrom,
							endDate: props.effectiveDateRangeTo,
						}}
						onOpenAction={() => setSelectorOpen(true)}
						onCloseAction={() => setSelectorOpen(false)}
					/>
				);
			},
		},
		{
			...columnProps,
			field: "reward",
			headerName: "Reward",
			align: "right",
			resizable: false,
			cellClassName: "no-focus cell-with-dollar-symbol",
			editable: isEditable && editSpiffAward,
			renderEditCell: (params) => <InputEditInputCell {...params} />,
			preProcessEditCellProps: (params) => {
				return { ...params.props, error: !params.props.value || isNaN(params.props.value) || Number(params.props.value) < 0.01 };
			},
			valueFormatter: (value) => accountFormat(value),
		},
		{
			field: "actions",
			type: "actions",
			width: 85,
			resizable: false,
			getActions: ({ id }) => {
				const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;
				if (isInEditMode) {
					return [
						<GridActionsCellItem
							key="saveSpiffBonus"
							icon={<SaveIconComponent />}
							disabled={selectorOpen}
							label="Save"
							onClick={() => handleSaveClickBonus(id)}
						/>,
						<GridActionsCellItem
							key="cancel"
							icon={<CancelIcon />}
							disabled={selectorOpen}
							label="Cancel"
							className="textPrimary"
							onClick={() => handleCancelClick(id)}
							color="inherit"
						/>,
					];
				}

				return [];
			},
		},
	];

	useEffect(() => {
		const mappedRows =
			data?.flatMap((parent) =>
				(parent.awards || []).map((award) => ({
					...award,
					recipient: parent.recipient,
					vinNumbers: award.repairOrders?.flatMap((order) => order.vinNumber).join(", "),
				}))
			) || [];
		if (editModeActive) {
			const newRow = rows.find((row) => row.isNew);
			setRows(compact([newRow, ...mappedRows]));
		} else {
			setRows(compact(mappedRows));
		}
		return () => {
			setRows([]);
			setSelectedRows([]);
		};
	}, [data]);

	const mappedRows = rows.map((row) => ({
		...row,
		repairOrderNumbers: row?.repairOrders?.map((deal) => deal.repairOrderNumber),
		vinNumbers: row?.repairOrders?.map((deal) => deal.vinNumber),
	}));

	return (
		<DataSmartTable
			columns={columns}
			rows={mappedRows}
			rowHeight={28}
			apiRef={apiRef}
			editMode="row"
			emptyTableImageWidth="250px"
			emptyTableImageHeight="272px"
			maxRows={12}
			enableCustomScrollbars
			disableColumnMenu
			disableRowSelectionOnClick
			checkboxSelection
			isRowSelectable={() => !editModeActive && Boolean(editSpiffAward)}
			onRowSelectionModelChange={handleRowSelected}
			rowModesModel={rowModesModel}
			processRowUpdate={handleProcessRowUpdateBonus}
			onProcessRowUpdateError={handleProcessRowUpdateBonusError}
			onRowModesModelChange={(newModel) => handleRowModesModelChange(newModel)}
			onRowEditStart={handleRowEditStartBonus}
			onRowEditStop={handleRowEditStop}
			columnVisibilityModel={{
				_id: false,
				repairOrders: false,
			}}
			slots={{
				toolbar: CommonToolbar,
				noRowsOverlay: NoRowsOverlay,
			}}
			autoHeight={false}
			slotProps={{
				toolbar: {
					customComponent: <HelpTooltip title={HELP_TOOLTIP_TITLE} />,
					filter: {
						hide: !!selectedRows.length,
						disabled: editModeActive,
					},
					search: {
						columns,
						data: rows,
						hide: !!selectedRows.length,
						disabled: editModeActive,
					},
					add: {
						onAddAction: handleAddRow,
						hide: !!selectedRows.length,
						disabled: editModeActive || !(isEditable && editSpiffAward) || isLoading || rowLoading,
					},
					delete: {
						onDeleteAction: handleDeleteClick,
						deleteTitle: "Remove",
						hide: !selectedRows.length,
						disabled: !(isEditable && editSpiffAward) || isLoading || rowLoading,
					},
				},
				noRowsOverlay: {
					title: "No Spiff Awards Data",
				},
			}}
			hideFooter
			customProps={{
				tableContainerClassName: "!h-[390px]",
				tableClassName: {
					"functional-table": true,
					"edit-table": true,
					...TAILWIND_FOR_DATA_SMART_TABLE,
					"!top-[-36px]": true,
				},
				expandableRows: false,
				hide: !rows?.length,
				tableHidedProps: {
					currentMonth: isEditable && editSpiffAward && isGenericSpiff,
					sectionFirstTitle: "No Awards in this list.",
					onAddTheFirstOne: handleAddRow,
				},
			}}
		/>
	);
}

const MoreCell = (props) => {
	const [anchorEl, setAnchorEl] = useState(null);
	const open = Boolean(anchorEl);
	const formattedValue =
		props?.row?.repairOrders?.map((item) => {
			return props.field === "repairOrderNumbers" ? item.repairOrderNumber : item.vinNumber;
		}) ?? [];
	const hasMoreThan = formattedValue?.length > props.visibleAmount;
	const moreText = hasMoreThan ? `${formattedValue?.length - props.visibleAmount} more` : "";
	const displayText = formattedValue?.slice(0, props.visibleAmount)?.join(", ");

	const handleClick = (event) => {
		if (hasMoreThan) {
			setAnchorEl(event.currentTarget);
		}
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	return (
		<>
			<Box className="!font-sans !text-[12px] flex flex-row !items-center">
				{displayText}
				{hasMoreThan && ","}
				&nbsp;
				{hasMoreThan && (
					<Typography
						className={clsx("!font-sans !text-[12px]", "!text-primary !font-bold underline cursor-pointer")}
						onClick={handleClick}
					>
						{moreText}
					</Typography>
				)}
			</Box>
			<Menu
				anchorEl={anchorEl}
				open={open}
				onClose={handleClose}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "center",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "center",
				}}
				slotProps={{
					paper: {
						className: "!rounded-xl",
					},
				}}
			>
				<Box className="px-4 py-1">
					{formattedValue.map((item, index) => (
						<Typography key={item} className="!font-sans-bold !text-gray-text !text-[12px] !mb-1">
							{index + 1}.&nbsp;
							<Typography className="!font-sans !text-gray-text !text-[12px]" component="span">
								{item}
							</Typography>
						</Typography>
					))}
				</Box>
			</Menu>
		</>
	);
};

export default function SpiffBonusEntryTable(props) {
	const { loading, locationId, dataPresent } = props;

	if (loading || !locationId) {
		return null;
	}
	return <>{dataPresent && <SpiffBonusTable {...props} />}</>;
}

SpiffBonusEntryTable.propTypes = {
	loading: PropTypes.bool,
	locationId: PropTypes.string,
	data: PropTypes.array,
	dataPresent: PropTypes.bool,
	isEditable: PropTypes.bool,
	definitionId: PropTypes.string,
	spiffDocumentId: PropTypes.string,
	effectiveDateRangeFrom: PropTypes.string,
	effectiveDateRangeTo: PropTypes.string,
	commissionCycleId: PropTypes.string,
	currentMonth: PropTypes.bool,
};
